import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import './ResizableDivs.css';
import { AppCriteriaField, AppSkinProps } from '../../types/decisions';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Image from '../image/Image';
import { useSearchParams } from 'react-router-dom';

interface CriteriaItem extends AppCriteriaField {
    name: string;
    height: number;
    width: number;
    isHidden: boolean;
    importance: number;
}

interface Props {
    initialCriteriaFields: AppCriteriaField[];
    onCriteriaApply: AppSkinProps['onCriteriaApply'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange'];
    onSliderBulkChange: AppSkinProps['onSliderBulkChange'];
    onCriteriaSettingsOpen: (id: string) => void;
    criteriaFields: AppSkinProps['criteriaData']['fields'];
    onAutoPopupCheck?: (newImportance: number, id: string, callback?: () => void) => void;
}

const MIN_VISIBLE_PERCENTAGE = 7;
const POPUP_PERCENTAGE = 30;

const generateCriteriaInitialInfo = (initialCriteriaFields: AppCriteriaField[], containerWidth: number): CriteriaItem[] => {
    const visibleFields = initialCriteriaFields.filter(item => !item.hide_importance);
    const totalImportanceRatio = visibleFields.reduce((acc, item) => acc + +item.importance, 0) / 100;

    return visibleFields.map((item) => {
        const importance = Math.round(+item.importance / totalImportanceRatio);
        return {
            ...item,
            name: typeof item.name === 'object' ? item.name.value : item.name,
            height: 120,
            width: importance * containerWidth / 100,
            isHidden: importance < MIN_VISIBLE_PERCENTAGE,
            importance: importance,
        };
    });
};

const ResizableDivs = ({
    onSliderBulkChange,
    onCriteriaApply,
    onCriteriaSettingsOpen,
    criteriaFields,
    onAutoPopupCheck,
}: Props) => {
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const [ criteria, setCriteria ] = useState<CriteriaItem[]>([]);
    const [ poppedUpIds, setPoppedUpIds ] = useState<string[]>([]);
    const [ editingCriteriaId, setEditingCriteriaId ] = useState<string>('');
    const containerRef = useRef<HTMLDivElement>(null);
    const [ isProductRefreshNeeded, setProductRefreshNeeded ] = useState(false);
    const visibleCriteria = useMemo(() => criteria.filter(item => !item.isHidden), [ criteria ]);
    const hiddenCriteria = useMemo(() => criteria.filter(item => item.isHidden), [ criteria ]);

    useEffect(() => {
        debugger;
        const items = generateCriteriaInitialInfo(criteriaFields, containerRef.current?.clientWidth || 1000);
        setCriteria(items);
        const importantCriteria: string[] = [];
        items.forEach(item => {
            if (item.importance > POPUP_PERCENTAGE) {
                importantCriteria.push(item.id);
            }
        });
        setPoppedUpIds(importantCriteria);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ criteriaFields ]);

    useEffect(() => {
        if (!isProductRefreshNeeded) {
            return;
        }

        onBoxResizeStop();
        setProductRefreshNeeded(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ hiddenCriteria.length ]);

    const onBoxResize = (size: ResizeCallbackData['size'], id: string) => {
        const { width } = size;
        const importance = Math.round(width * 100 / (
            containerRef.current?.clientWidth || 1000
        ));

        setEditingCriteriaId(id);
        setCriteria(sizes => {
            const newSizes = sizes.map((item) => {
                if (item.id === id) {
                    if (importance >= POPUP_PERCENTAGE && !poppedUpIds.includes(item.id)) {
                        setPoppedUpIds(ids => [ ...ids, item.id ]);
                    }

                    return {
                        ...item,
                        width,
                        importance,
                        isHidden: importance < MIN_VISIBLE_PERCENTAGE,
                    };
                }

                return item;
            });

            setProductRefreshNeeded(true);
            return newSizes;
        });
    };

    const onBoxResizeStop = () => {
        const containerWidth = containerRef.current?.clientWidth || 1000;
        const elementSizes: {
            [key: string]: number
        } = {};
        containerRef.current?.childNodes.forEach((childNode) => {
            // @ts-ignore
            const elementWidth: number = childNode.clientWidth || 0;
            // @ts-ignore
            const elementId = childNode.dataset.elementid;
            elementSizes[elementId] = Math.round(elementWidth * 100 / containerWidth);
        });

        onSliderBulkChange(elementSizes);
        const currentItemImportance = elementSizes[editingCriteriaId] || 0;
        if (currentItemImportance) {
            const currentCriteriaIndex = criteria.findIndex(item => item.id === editingCriteriaId);
            if (-1 !== currentCriteriaIndex && onAutoPopupCheck) {
                onAutoPopupCheck(currentItemImportance, editingCriteriaId, () => onCriteriaApply(dataset));
            }
        } else {
            onCriteriaApply(dataset);
        }
    };

    const onOpenSettings = (id: string) => {
        onCriteriaSettingsOpen(id);
    };

    const makeCriteriaVisible = (id: string) => {
        setCriteria(criteria => criteria.map((item) => {
            if (id !== item.id) {
                return item;
            }

            return {
                ...item,
                importance: MIN_VISIBLE_PERCENTAGE,
                width: (
                    containerRef.current?.clientWidth || 1000
                ) * MIN_VISIBLE_PERCENTAGE / 100,
                isHidden: false,
            };
        }));
    };

    debugger;
    return (
        <div>
            <div className="layoutRoot" ref={containerRef}>
                {visibleCriteria.map((item) => (
                    // @ts-ignore
                    <Resizable
                        key={item.id}
                        className="box"
                        height={item.height}
                        width={item.width}
                        onResize={(_, data) => onBoxResize(data.size, item.id)}
                        onResizeStop={onBoxResizeStop}
                        resizeHandles={[ 'e', 'w' ]}
                        axis="both"
                    >
                        <div data-elementid={item.id} style={{
                            width: item.width + '%',
                            height: item.height + 'px',
                        }}>
                            <h3 className="importance-card-title">
                                <span style={{ fontSize: item.importance > 20 ? 18.75 : 14 }}>{item.name}</span>
                                <IconButton style={{ marginLeft: '.5rem' }} onClick={() => onOpenSettings(item.id)}>
                                    <EditIcon color="primary" fontSize="small"/>
                                </IconButton>
                            </h3>
                            <Image src={item.icon} className="app-criteria-icon"/>
                        </div>
                    </Resizable>
                ))}
            </div>
            <div className="skin5-bottom-actions">
                <div className="hidden-elements">
                    <div className="hidden-elements-items-container">
                        <div>
                            {hiddenCriteria.map((item) => (
                                <span
                                    key={item.id}
                                    className="hidden-elements-item"
                                    onClick={() => makeCriteriaVisible(item.id)}
                                    title="Click to make it visible"
                                >
                                    {item.name}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResizableDivs;
